import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { FaEdit, FaEnvelope, FaPlus, FaTrash } from "react-icons/fa";
import { GlobalContext } from "../Context/GlobalState";
import { BASE_URL_API_TRANXT_UAT_API } from "../utils/Constant";
import { basicAuth } from "../utils/Credentials";
import "bootstrap/dist/css/bootstrap.min.css";

const initialForm = {
  name: "",
  email: "",
  password: "",
  mobile: "",
  department: "",
  status: "Active",
};

const getCleanValue = (value) => {
  const normalized = value?.toString().trim();
  if (!normalized || normalized === "undefined" || normalized === "null") {
    return "";
  }
  return normalized;
};

const getStoredSiteObj = () => {
  try {
    return JSON.parse(sessionStorage.getItem("SITE_OBJECT")) || {};
  } catch (error) {
    return {};
  }
};

const EmailData = () => {
  const { siteid, siteurl, Productname, siteObj, user } =
    useContext(GlobalContext);
  const { password, username } = basicAuth;
  const base64Credentials = btoa(`${username}:${password}`);
  const [emailData, setEmailData] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [swal, setSwal] = useState({
    show: false,
    type: "success",
    title: "",
    text: "",
    showCancel: false,
  });

  const storedSiteObj = useMemo(() => getStoredSiteObj(), []);
  const projectLabel = Productname || siteurl || "Selected Property";
  const projectToken =
    getCleanValue(siteObj?.p_token) ||
    getCleanValue(siteObj?.project_token) ||
    getCleanValue(storedSiteObj?.p_token) ||
    getCleanValue(storedSiteObj?.project_token) ||
    getCleanValue(storedSiteObj?.id) ||
    getCleanValue(sessionStorage.getItem("selectedURLId")) ||
    getCleanValue(siteid) ||
    getCleanValue(siteObj?.id) ||
    "";
  const currentSiteUrl =
    getCleanValue(siteurl) && siteurl !== "Select Url"
      ? siteurl
      : getCleanValue(storedSiteObj?.value);
  const userId = user?.user_id?.toString() || "";
  const hasSelectedProject = Boolean(projectToken);

  const showSwal = useCallback((text, type = "success", options = {}) => {
    setSwal({
      show: true,
      type,
      title: options.title || (type === "error" ? "Error" : "Success"),
      text,
      showCancel: Boolean(options.showCancel),
    });
  }, []);

  const closeSwal = () => {
    setSwal((prev) => ({ ...prev, show: false }));
    setPendingDeleteId(null);
  };

  const buildPayload = useCallback(
    (step, overrides = {}) => ({
      step,
      id: 0,
      fk_userid: userId,
      project_token: projectToken,
      site_url: currentSiteUrl,
      name: "",
      email: "",
      password: "",
      mobile: "",
      department: "",
      status: "",
      ...overrides,
    }),
    [currentSiteUrl, projectToken, userId]
  );

  const callEmailApi = useCallback(async (payload) => {
    const response = await fetch(
      `${BASE_URL_API_TRANXT_UAT_API}/api/UserInfo/InsertEmaiCredential`,
      {
        method: "POST",
        headers: {
          Authorization: `Basic ${base64Credentials}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    return response.json();
  }, [base64Credentials]);

  const getApiMessage = (data, fallback) =>
    data?.data?.[0]?.message || data?.data?.[0]?.Message || fallback;

  const getEmailData = useCallback(async () => {
    if (!userId || !hasSelectedProject) {
      setEmailData([]);
      return;
    }

    setLoading(true);
    try {
      const data = await callEmailApi(buildPayload("5"));

      if (data?.status === "0" && Array.isArray(data?.data)) {
        const filteredData = data.data.filter((item) => {
          const itemProjectToken = item?.project_token?.toString();
          const itemSiteUrl = item?.site_url?.toString();

          return (
            (projectToken && itemProjectToken === projectToken) ||
            (currentSiteUrl && itemSiteUrl === currentSiteUrl)
          );
        });

        setEmailData(filteredData);
      } else {
        setEmailData([]);
        showSwal(data?.msg || "Unable to fetch email records.", "error");
      }
    } catch (error) {
      setEmailData([]);
      showSwal("Unable to fetch email records.", "error");
    } finally {
      setLoading(false);
    }
  }, [
    buildPayload,
    callEmailApi,
    currentSiteUrl,
    hasSelectedProject,
    projectToken,
    showSwal,
    userId,
  ]);

  useEffect(() => {
    setFormData(initialForm);
    setEditingId(null);
    getEmailData();
  }, [getEmailData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) return;
    if (!projectToken) {
      showSwal("Please select a project with a valid project token.", "error");
      return;
    }

    setLoading(true);
    try {
      const data = await callEmailApi(
        buildPayload(editingId ? "2" : "1", {
          id: editingId || 0,
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password.trim(),
          mobile: formData.mobile.trim(),
          department: formData.department.trim(),
          status: formData.status,
        })
      );

      if (data?.status === "0") {
        showSwal(
          getApiMessage(
            data,
            `Email data ${editingId ? "updated" : "saved"} successfully.`
          )
        );
        setFormData(initialForm);
        setEditingId(null);
        await getEmailData();
      } else {
        showSwal(data?.msg || "Unable to save email data.", "error");
      }
    } catch (error) {
      showSwal("Unable to save email data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name,
      email: item.email,
      password: item.password || "",
      mobile: item.mobile || "",
      department: item.department,
      status: item.status,
    });
  };

  const handleDelete = (id) => {
    setPendingDeleteId(id);
    showSwal("Are you sure you want to delete this email data?", "warning", {
      title: "Confirm Delete",
      showCancel: true,
    });
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;

    const deleteId = pendingDeleteId;
    setSwal((prev) => ({ ...prev, show: false }));
    setLoading(true);
    try {
      const data = await callEmailApi(
        buildPayload("3", {
          id: deleteId,
          fk_userid: "",
          project_token: "",
          site_url: "",
        })
      );

      if (data?.status === "0") {
        showSwal(getApiMessage(data, "Email data deleted."));
        if (editingId === deleteId) {
          setEditingId(null);
          setFormData(initialForm);
        }
        await getEmailData();
      } else {
        showSwal(data?.msg || "Unable to delete email data.", "error");
      }
    } catch (error) {
      showSwal("Unable to delete email data.", "error");
    } finally {
      setPendingDeleteId(null);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData(initialForm);
  };

  return (
    <div className="email-data-page">
      <section className="email-data-header">
        <div>
          <span>Email Data</span>
          <h3>Manage Property Emails</h3>
          <p>
            Add and manage email records for{" "}
            <strong>{hasSelectedProject ? projectLabel : "your selected property"}</strong>.
          </p>
        </div>
        <div className="email-data-header-icon">
          <FaEnvelope />
        </div>
      </section>

      {!hasSelectedProject && (
        <div className="email-data-alert">
          Please select a project/property from the top navbar before adding
          email data.
        </div>
      )}

      {swal.show && (
        <div className="email-data-swal-backdrop">
          <div className="email-data-swal">
            <div className={`email-data-swal-icon ${swal.type}`}>
              {swal.type === "warning" ? "!" : swal.type === "error" ? "x" : "i"}
            </div>
            <h4>{swal.title}</h4>
            <p>{swal.text}</p>
            <div className="email-data-swal-actions">
              {swal.showCancel && (
                <button
                  type="button"
                  className="email-data-swal-btn cancel"
                  onClick={closeSwal}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className={`email-data-swal-btn ${swal.type}`}
                onClick={swal.showCancel ? confirmDelete : closeSwal}
              >
                {swal.showCancel ? "Yes, Delete" : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Row className="email-data-layout">
        <Col lg={4} className="mb-3">
          <div className="email-data-card">
            <div className="email-data-card-head">
              <h5>{editingId ? "Update Email" : "Add Email"}</h5>
              <p>Store contact details for the current property.</p>
            </div>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  disabled={!hasSelectedProject}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  disabled={!hasSelectedProject}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="text"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter email password"
                  disabled={!hasSelectedProject}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Mobile</Form.Label>
                <Form.Control
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  disabled={!hasSelectedProject}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Department</Form.Label>
                <Form.Control
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="Marketing, SEO, Support..."
                  disabled={!hasSelectedProject}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={!hasSelectedProject}
                >
                  <option>Active</option>
                  <option>Inactive</option>
                </Form.Select>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  className="email-data-submit"
                  disabled={!hasSelectedProject || loading}
                >
                  <FaPlus /> {editingId ? "Update" : "Add"}
                </Button>
                {editingId && (
                  <Button variant="outline-secondary" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </Form>
          </div>
        </Col>

        <Col lg={8}>
          <div className="email-data-card">
            <div className="email-data-table-head">
              <div>
                <h5>Email Records</h5>
                <p>
                  {loading
                    ? "Loading records..."
                    : `${emailData.length} saved contacts for this property.`}
                </p>
              </div>
            </div>

            <div className="email-data-table-wrap">
              <Table responsive hover className="email-data-table mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Password</th>
                    <th>Mobile</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {emailData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="email-data-empty">
                        No email data added yet.
                      </td>
                    </tr>
                  ) : (
                    emailData.map((item) => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.password || "-"}</td>
                        <td>{item.mobile || "-"}</td>
                        <td>{item.department || "-"}</td>
                        <td>
                          <span
                            className={`email-data-status ${
                              item.status === "Active" ? "active" : "inactive"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            className="email-data-action edit"
                            type="button"
                            onClick={() => handleEdit(item)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="email-data-action delete"
                            type="button"
                            disabled={loading}
                            onClick={() => handleDelete(item.id)}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default EmailData;
